import { useState } from 'react'

import { BlankSlate, Box, Button, SearchField, Title } from '@factorialco/deprecated-design-system'
import { BankAccountPageDocument, BankAccountPageQuery } from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import Filter from 'modules/core/components/Filter'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { formattedBankAccountNumber } from 'modules/banking/utils'
import { BankAccountRow } from 'modules/finance/components/Accounting/BankAccount/BankAccountRow'
import PageLoading from 'modules/layout/components/Page/Loading'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const NoAccountsBlankSlate = () => {
  const { navigation } = useNavigation()

  return (
    <Box flexGrow justifyContent="center">
      <BlankSlate
        illustration="alert"
        size="small"
        title={i18n.t('finance.settings.accounting.expense_category.blank_slate.title')}
        description={i18n.t('finance.settings.accounting.expense_category.blank_slate.description')}
      >
        <Button.Router to={navigation.finance.accounting.fullPath()}>
          {i18n.t('finance.settings.accounting.expense_category.blank_slate.cta')}
        </Button.Router>
      </BlankSlate>
    </Box>
  )
}

export type BankAccountLegalEntity = BankAccountPageQuery['companies']['legalEntities'][number]

const BankAccount = () => {
  const { search, onSearch } = useDebouncedSearch()
  const [legalEntity, setLegalEntity] = useState<BankAccountLegalEntity>()

  const { data, loading, error } = useQuery(BankAccountPageDocument, {
    variables: { types: ['bank'] },
    onCompleted: (completedData) => {
      const mainLegalEntity = completedData.companies.legalEntities.find((node) => node.main)
      if (mainLegalEntity) setLegalEntity(mainLegalEntity)
    },
  })

  const canSeeLedgerAccountSettings =
    data?.finance?._policies?.settingsSeeAccountingSettingsLedgerAccounts?.can ?? false
  const canManage = data?.finance?._policies?.accountingManageAccountResources?.can ?? false
  const legalEntities = data?.companies?.legalEntities ?? []
  const bankAccounts =
    legalEntity?.bankingAccounts?.map((bankingAccount) => bankingAccount.bankAccounts).flat() ?? []
  const ledgerAccounts = legalEntity?.financeAccounts ?? []

  const filteredBankAccounts = sortBy(
    bankAccounts.filter(
      (bankAccount) =>
        bankAccount.accountAlias?.toLowerCase().includes(search.toLowerCase()) ||
        formattedBankAccountNumber(bankAccount).toLowerCase().includes(search.toLowerCase())
    ),
    ['accountAlias']
  )

  return (
    <ResponsivePage.Body authorized={canSeeLedgerAccountSettings} loading={loading} error={error}>
      <Box height="full">
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
          paddingTop={'s24'}
        >
          {legalEntity && (
            <Box>
              <Filter.SingleChoice
                label={i18n.t('finance.workspace.legal_entity_scope.selector_label')}
                options={legalEntities.map((le) => ({ label: le.legalName, value: le.id }))}
                value={legalEntity.id}
                onValueChange={(id) => {
                  const selectedLegalEntity = legalEntities.find((le) => le.id === id)
                  setLegalEntity(selectedLegalEntity)
                }}
                noFooter
              />
            </Box>
          )}
          {ledgerAccounts.length > 0 && (
            <Box width="fit-content">
              <SearchField
                variant="regular"
                placeholder={i18n.t('common.search')}
                search={search}
                onSearch={onSearch}
              />
            </Box>
          )}
        </Box>

        {ledgerAccounts.length === 0 &&
          (loading ? (
            <Box flexGrow justifyContent="center">
              <PageLoading />
            </Box>
          ) : (
            <NoAccountsBlankSlate />
          ))}

        {ledgerAccounts.length > 0 && legalEntity && (
          <>
            <Box
              flexDirection="row"
              gap="s16"
              justifyContent="spaceBetween"
              alignItems="flexStart"
              paddingTop={'s64'}
            >
              <Box width="s600">
                <Title title={i18n.t('finance.settings.accounting.bank_account_title')}></Title>
              </Box>
              <Box width="full">
                <Title title={i18n.t('finance.settings.accounting.assigned_accounts')}></Title>
              </Box>
            </Box>

            <Box>
              {filteredBankAccounts.map((bankAccount) => (
                <BankAccountRow
                  key={bankAccount.id}
                  accounts={ledgerAccounts}
                  legalEntity={legalEntity}
                  bankAccount={bankAccount}
                  canManage={canManage}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default BankAccount
